import type { FormFragment } from '@seek/cmsu-cms-connect';
import { createForm } from '@seek/forms-ui';
import type { Country, SiteName } from '@seek/melways-sites';
import { VocabProvider } from '@vocab/react';
import { Alert, ContentBlock, Text } from 'braid-design-system';

import { useCMSUContext } from '../../hooks/useCMSUContext';
import type { ButtonStyleProps } from '../types';

import { Form } from './Form';
import { getValidFieldKey } from './helper';
import type { DynamicFormValues, FormField, FormWithActualKey } from './types';

const createFormValues = (fields: FormField[]): DynamicFormValues => {
  let formValues: DynamicFormValues = {};

  fields.forEach((field) => {
    const key = getValidFieldKey(field);

    if (field.__typename === 'FormCheckboxGroup') {
      formValues[key] = field.options.reduce(
        (acc, option: { name: string | number }) => ({
          ...acc,
          [option.name]: false,
        }),
        {},
      );
      // key values are inside the keyValues, so we flat it out outside
    } else if (field.__typename === 'FormHiddenFieldGroup') {
      const hiddenFieldKeyValues = field.keyValues.reduce(
        (acc, keyValue) => ({
          ...acc,
          [keyValue.key]: keyValue.value,
        }),
        {},
      );
      formValues = {
        ...formValues,
        ...hiddenFieldKeyValues,
      };
    } else {
      formValues[key] = '';
    }
  });
  return formValues;
};

const createFieldActualKey = (form: FormFragment) =>
  form.fields?.map((field) => ({
    ...field,
    actualKey: getValidFieldKey(field),
  })) || [];

export const CForm = ({
  locale,
  sourceName,
  privacyUrl,
  recaptchaKey,
  country,
  site,
  wptSalesForceApi,
  form,
  sectionName,
}: {
  locale: string;
  sourceName: string;
  privacyUrl: string;
  recaptchaKey: string;
  country: Country;
  site: SiteName;
  wptSalesForceApi: string;
  submitBtnStyle?: ButtonStyleProps;
  form: FormFragment;
  sectionName?: string;
}) => {
  const { FormProvider, useField } = createForm<DynamicFormValues>();
  const { language } = useCMSUContext();
  const formValues: DynamicFormValues = createFormValues(
    form && form.fields ? form.fields : [],
  );
  const initialValues: DynamicFormValues | null = formValues;
  const formWithActualKey: FormWithActualKey = {
    ...form,
    fields: createFieldActualKey(form),
  };
  return (
    <ContentBlock>
      <VocabProvider language={language}>
        {form ? (
          <FormProvider initialValues={initialValues}>
            {(formProps) => (
              <Form
                {...formProps}
                locale={locale}
                useField={useField}
                recaptchaKey={recaptchaKey}
                country={country}
                site={site}
                sourceName={sourceName}
                privacyUrl={privacyUrl}
                wptSalesForceApi={wptSalesForceApi}
                form={formWithActualKey}
                sectionName={sectionName}
              />
            )}
          </FormProvider>
        ) : (
          <Alert tone="caution">
            <Text>
              If you&apos;re seeing this, the form might not be published
            </Text>
          </Alert>
        )}
      </VocabProvider>
    </ContentBlock>
  );
};
